import { CARDS_ESPECIAIS_CATALOG_ID, PREMIOS_CATALOG_IDS } from "utils/constants";
import { api } from "./api";

const COLECAO_VISUALIZADOS = "premios_visualizados__c";

/** Catálogos que geram aviso de entrega ao participante. */
const CATALOGOS_AVISADOS = [...PREMIOS_CATALOG_IDS, CARDS_ESPECIAIS_CATALOG_ID];

/** Marca gravada pelo trigger "Resgate automático de prêmios" (FAMb0cR). */
const ORIGEM_RESGATE_AUTOMATICO = "resgate_automatico";

/**
 * O card especial precisa de um aviso próprio: ele ainda não é o prêmio, e só
 * vira prêmio quando o participante clica em RESGATAR na vitrine.
 */
export type TipoEntrega = "premio" | "card";

export interface PremioRecebido {
  /** Id do achievement da entrega — identifica o prêmio recebido de forma única. */
  _id: string;
  item: string;
  time: number;
  nome: string;
  descricao?: string;
  imagem?: string;
  tipo: TipoEntrega;
}

/**
 * Entregas que o participante ainda não viu: prêmios que caíram pelo resgate
 * automático e cards especiais que continuam na vitrine.
 *
 * Todos os filtros ficam no próprio pipeline para que a lista chegue pronta —
 * sem uma segunda consulta e sem cruzamento no cliente.
 */
export async function getPremiosNaoVisualizados(
  player: string
): Promise<PremioRecebido[]> {
  const payload = [
    { $match: { player, type: 2, total: { $gt: 0 } } },
    {
      $lookup: {
        from: "catalog_item",
        localField: "item",
        foreignField: "_id",
        as: "ci",
      },
    },
    { $unwind: "$ci" },
    { $match: { "ci.catalogId": { $in: CATALOGOS_AVISADOS } } },
    // quem resgatou pelo botão já viu a confirmação na hora; repetir o aviso no
    // próximo acesso só confunde. Prêmio só é anunciado quando caiu sozinho.
    // O card não passa por essa regra: ele vem da recompensa do desafio e o
    // participante não recebe nenhum aviso na hora.
    {
      $match: {
        $or: [
          { "ci.catalogId": CARDS_ESPECIAIS_CATALOG_ID },
          { "extra.origem": ORIGEM_RESGATE_AUTOMATICO },
        ],
      },
    },
    // o resgate do card especial não apaga o lançamento de entrega, apenas
    // acrescenta a baixa (-1). Sem conferir o saldo, o card trocado voltaria a
    // ser anunciado como se o participante ainda o tivesse na vitrine.
    {
      $lookup: {
        from: "achievement",
        let: { jogador: "$player", entregue: "$item" },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ["$player", "$$jogador"] },
                  { $eq: ["$item", "$$entregue"] },
                  { $eq: ["$type", 2] },
                ],
              },
            },
          },
          { $group: { _id: null, saldo: { $sum: "$total" } } },
        ],
        as: "saldoItem",
      },
    },
    { $match: { "saldoItem.saldo": { $gt: 0 } } },
    {
      $lookup: {
        from: COLECAO_VISUALIZADOS,
        localField: "_id",
        foreignField: "achievement",
        as: "visto",
      },
    },
    { $match: { visto: { $size: 0 } } },
    {
      $project: {
        _id: 1,
        item: 1,
        time: 1,
        nome: "$ci.name",
        descricao: "$ci.description",
        imagem: "$ci.image.medium.url",
        tipo: {
          $cond: [
            { $eq: ["$ci.catalogId", CARDS_ESPECIAIS_CATALOG_ID] },
            "card",
            "premio",
          ],
        },
      },
    },
    { $sort: { time: 1 } },
  ];

  const { data } = await api.post("/database/achievement/aggregate", payload);
  return Array.isArray(data) ? data : [];
}

/**
 * Registra que o participante viu o prêmio.
 * O _id é derivado de player + achievement, então repetir a chamada (outra aba,
 * retry de rede) grava o mesmo documento em vez de duplicar o registro.
 */
export async function marcarPremioComoVisualizado(
  player: string,
  premio: PremioRecebido
) {
  const registro = {
    _id: `${player}_${premio._id}`,
    player,
    achievement: premio._id,
    item: premio.item,
    time: { $date: new Date().getTime() },
  };

  await api.put(`/database/${COLECAO_VISUALIZADOS}?strict=true`, registro);
}
